import React, {FC, useEffect, useRef, useState} from "react";
import {BubbleMenu, Editor, EditorContent, useEditor} from "@tiptap/react";
import {TextSelection} from "@tiptap/pm/state";
import {Button, Input, Popover, Space, Spin, Tooltip, Upload, message} from "antd";
import {
    BoldOutlined,
    DeleteOutlined,
    FontSizeOutlined,
    ItalicOutlined,
    LinkOutlined,
    OrderedListOutlined,
    PicCenterOutlined,
    PicLeftOutlined,
    PicRightOutlined,
    PictureOutlined,
    UnorderedListOutlined,
} from "@ant-design/icons";
import classnames from "classnames";
import i18n from "../../services/i18n";
import {MAX_CONTENT_IMAGE_UPLOAD_BYTES} from "../../../shared/const";
import {ImageAlign, ImageLayout, ImageSize, layoutTitle, parseImageLayout} from "../TextAreaInputMarkdown/markdownImage";
import {createRichTextExtensions, getMarkdown, insertImageAt, normalizeLegacyMarkdown, splitLinesInSelection, trackPosition} from "./richTextExtensions";
import css from "./RichTextEditor.less";

const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg"];
/** Neue Bilder starten klein rechts neben dem Text: der häufigste Fall ist ein Siegel oder Logo. */
const DEFAULT_IMAGE_LAYOUT: ImageLayout = {size: "klein", align: "rechts"};

type RichTextEditorProps = {
    /** Markdown, wie es gespeichert wird. */
    value: string;
    onChange: (markdown: string) => void;
    onBlur?: () => void;
    placeholder?: string;
    /** Ohne Upload-Funktion gibt es keinen Bild-Knopf. Liefert die öffentliche URL. */
    onUploadImage?: (file: File) => Promise<string>;
    minHeight?: number;
    testId?: string;
};

const imageFile = (files: FileList | null | undefined): File | undefined => Array.from(files || []).find((file) => file.type.startsWith("image/"));

const ToolbarButton: FC<{title: string; active?: boolean; onClick: () => void; icon: React.ReactNode; testId?: string}> = ({title, active, onClick, icon, testId}) => (
    <Tooltip title={title}>
        <Button
            type="text"
            size="small"
            icon={icon}
            aria-label={title}
            aria-pressed={active}
            className={classnames({[css.active]: active})}
            // mousedown verhindern, sonst verliert der Editor die Auswahl vor dem Klick.
            onMouseDown={(event) => event.preventDefault()}
            onClick={onClick}
            data-testid={testId}
        />
    </Tooltip>
);

const LinkButton: FC<{editor: Editor}> = ({editor}) => {
    const tr = i18n.containers.courses.CourseCreate.courseWebsiteInfo.toolbar;
    const [open, setOpen] = useState(false);
    const [text, setText] = useState("");
    const [href, setHref] = useState("");

    const prefill = () => {
        // Bestehender Link: ganzen Link markieren, damit Text und Adresse zusammen bearbeitet werden.
        if (editor.isActive("link")) {
            editor.chain().extendMarkRange("link").run();
        }
        const {from, to} = editor.state.selection;

        setText(editor.state.doc.textBetween(from, to, " "));
        setHref(editor.getAttributes("link").href || "");
    };

    const apply = () => {
        const url = href.trim();
        const label = text.trim() || url;
        const {from, to} = editor.state.selection;

        if (!url) {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
        } else if (from !== to && editor.state.doc.textBetween(from, to, " ") === label) {
            // Text unverändert: nur die Adresse setzen, Formatierung (z. B. fett) bleibt.
            editor.chain().focus().setLink({href: url}).run();
        } else {
            editor
                .chain()
                .focus()
                .insertContentAt({from, to}, {type: "text", text: label, marks: [{type: "link", attrs: {href: url}}]})
                .run();
        }
        setOpen(false);
    };

    return (
        <Popover
            trigger="click"
            open={open}
            onOpenChange={(next) => {
                if (next) {
                    prefill();
                }
                setOpen(next);
            }}
            content={
                <div style={{width: 300}} data-testid="richTextLinkForm">
                    <div style={{fontSize: 12, marginBottom: 2}}>{tr.linkText()}</div>
                    <Input size="small" placeholder={tr.linkTextPlaceholder()} value={text} onChange={(event) => setText(event.target.value)} onPressEnter={apply} aria-label={tr.linkText()} />
                    <div style={{fontSize: 12, margin: "8px 0 2px"}}>{tr.linkHref()}</div>
                    <Input size="small" placeholder={tr.linkPlaceholder()} value={href} onChange={(event) => setHref(event.target.value)} onPressEnter={apply} aria-label={tr.linkHref()} />
                    <Space style={{marginTop: 10}}>
                        <Button size="small" type="primary" onClick={apply}>
                            {tr.linkApply()}
                        </Button>
                        {editor.isActive("link") && (
                            <Button
                                size="small"
                                onClick={() => {
                                    editor.chain().focus().extendMarkRange("link").unsetLink().run();
                                    setOpen(false);
                                }}
                            >
                                {tr.linkRemove()}
                            </Button>
                        )}
                    </Space>
                </div>
            }
        >
            <span>
                <ToolbarButton title={tr.link()} active={editor.isActive("link")} onClick={() => undefined} icon={<LinkOutlined />} />
            </span>
        </Popover>
    );
};

/**
 * Editor wie in WordPress/Notion für Markdown-Felder: Werkzeugleiste für Fett, Kursiv, Zwischenüberschrift,
 * Listen, Link und Bild. Ein Klick aufs Bild zeigt eine Leiste für Platz und Größe. Gespeichert wird Markdown.
 */
const RichTextEditor: FC<RichTextEditorProps> = ({value, onChange, onBlur, placeholder, onUploadImage, minHeight = 140, testId}) => {
    const trWebsite = i18n.containers.courses.CourseCreate.courseWebsiteInfo;
    const trImage = trWebsite.image;
    const trToolbar = trWebsite.toolbar;
    const [uploading, setUploading] = useState(false);
    // Zuletzt gemeldetes Markdown: unterscheidet eigene Änderungen von Werten, die von außen kommen
    // (Formular zurückgesetzt, Kursart-Vorlage übernommen).
    const lastMarkdown = useRef(value || "");
    const uploadRef = useRef<(file: File) => void>(() => undefined);

    const editor = useEditor({
        extensions: createRichTextExtensions(placeholder),
        content: normalizeLegacyMarkdown(value || ""),
        onUpdate: ({editor: current}) => {
            const markdown = getMarkdown(current.storage);

            lastMarkdown.current = markdown;
            onChange(markdown);
        },
        onBlur: () => onBlur && onBlur(),
        editorProps: {
            handlePaste: (_view, event) => {
                const file = imageFile(event.clipboardData && event.clipboardData.files);

                if (!file || !onUploadImage) {
                    return false;
                }
                event.preventDefault();
                uploadRef.current(file);

                return true;
            },
            handleDrop: (view, event) => {
                const file = imageFile(event.dataTransfer && event.dataTransfer.files);

                if (!file || !onUploadImage) {
                    return false;
                }
                event.preventDefault();
                // Cursor an die Ablagestelle setzen; der Upload fügt an der Cursorposition ein.
                const dropped = view.posAtCoords({left: event.clientX, top: event.clientY});

                if (dropped) {
                    view.dispatch(view.state.tr.setSelection(TextSelection.near(view.state.doc.resolve(dropped.pos))));
                }
                uploadRef.current(file);

                return true;
            },
        },
    });

    useEffect(() => {
        if (editor && (value || "") !== lastMarkdown.current) {
            lastMarkdown.current = value || "";
            editor.commands.setContent(normalizeLegacyMarkdown(value || ""), false);
        }
    }, [editor, value]);

    const uploadImage = async (file: File) => {
        if (!editor || !onUploadImage || uploading) {
            return;
        }
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            message.error(trImage.typeError());

            return;
        }
        if (file.size > MAX_CONTENT_IMAGE_UPLOAD_BYTES) {
            message.error(trImage.sizeError());

            return;
        }

        // Einfügestelle jetzt merken und mitführen: Wer während des Uploads klickt oder tippt, verschiebt sie nicht.
        const insertion = trackPosition(editor, editor.state.selection.from);

        setUploading(true);
        try {
            const src = await onUploadImage(file);
            const alt = (file.name || "").replace(/\.[^.]+$/, "") || trImage.defaultAlt();

            insertImageAt(editor, insertion.get(), {src, alt, title: layoutTitle(DEFAULT_IMAGE_LAYOUT)});
        } catch (error) {
            message.error(error instanceof Error ? error.message : trImage.uploadError());
        } finally {
            insertion.stop();
            setUploading(false);
        }
    };

    uploadRef.current = (file) => void uploadImage(file);

    if (!editor) {
        return null;
    }

    const layout = parseImageLayout(editor.getAttributes("image").title);
    const setLayout = (next: ImageLayout) =>
        editor
            .chain()
            .focus()
            .updateAttributes("image", {title: layoutTitle(next) || null})
            .run();
    const aligns: Array<{align: ImageAlign; title: string; icon: React.ReactNode}> = [
        {align: "links", title: trImage.alignLeft(), icon: <PicLeftOutlined />},
        {align: "mitte", title: trImage.alignCenter(), icon: <PicCenterOutlined />},
        {align: "rechts", title: trImage.alignRight(), icon: <PicRightOutlined />},
    ];
    const sizes: Array<{size: ImageSize; label: string}> = [
        {size: "klein", label: trImage.sizeSmall()},
        {size: "mittel", label: trImage.sizeMedium()},
        {size: "groß", label: trImage.sizeLarge()},
    ];

    return (
        <div className={css.root} data-testid={testId}>
            <div className={css.toolbar} role="toolbar">
                <ToolbarButton title={trToolbar.bold()} active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} icon={<BoldOutlined />} />
                <ToolbarButton title={trToolbar.italic()} active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} icon={<ItalicOutlined />} />
                <ToolbarButton title={trToolbar.heading()} active={editor.isActive("heading")} onClick={() => splitLinesInSelection(editor) && editor.chain().focus().toggleHeading({level: 3}).run()} icon={<FontSizeOutlined />} />
                <span className={css.divider} />
                <ToolbarButton title={trToolbar.bulletList()} active={editor.isActive("bulletList")} onClick={() => splitLinesInSelection(editor) && editor.chain().focus().toggleBulletList().run()} icon={<UnorderedListOutlined />} />
                <ToolbarButton
                    title={trToolbar.orderedList()}
                    active={editor.isActive("orderedList")}
                    onClick={() => splitLinesInSelection(editor) && editor.chain().focus().toggleOrderedList().run()}
                    icon={<OrderedListOutlined />}
                />
                <span className={css.divider} />
                <LinkButton editor={editor} />
                {onUploadImage && (
                    <Upload
                        accept={ALLOWED_IMAGE_TYPES.join(",")}
                        showUploadList={false}
                        disabled={uploading}
                        beforeUpload={(file) => {
                            void uploadImage(file);

                            // false: kein automatischer antd-Upload, wir laden selbst hoch.
                            return false;
                        }}
                    >
                        <Tooltip title={trImage.insertTooltip()}>
                            <Button type="text" size="small" icon={uploading ? <Spin size="small" /> : <PictureOutlined />} data-testid={testId && `${testId}-insertImage`}>
                                {uploading ? trImage.uploading() : trImage.insert()}
                            </Button>
                        </Tooltip>
                    </Upload>
                )}
            </div>

            <BubbleMenu editor={editor} shouldShow={({editor: current}) => current.isActive("image")} tippyOptions={{placement: "top", maxWidth: "none"}}>
                <div className={css.bubble} data-testid={testId && `${testId}-imageMenu`}>
                    {aligns.map(({align, title, icon}) => (
                        <Tooltip key={align} title={title}>
                            <Button
                                type="text"
                                size="small"
                                icon={icon}
                                aria-label={title}
                                className={classnames({[css.bubbleActive]: (layout.align || "mitte") === align})}
                                onClick={() => setLayout({...layout, align})}
                            />
                        </Tooltip>
                    ))}
                    <span className={css.bubbleDivider} />
                    {sizes.map(({size, label}) => (
                        <Button key={size} type="text" size="small" className={classnames({[css.bubbleActive]: layout.size === size})} onClick={() => setLayout({...layout, size})}>
                            {label}
                        </Button>
                    ))}
                    <span className={css.bubbleDivider} />
                    <Tooltip title={trImage.remove()}>
                        <Button type="text" size="small" icon={<DeleteOutlined />} aria-label={trImage.remove()} onClick={() => editor.chain().focus().deleteSelection().run()} />
                    </Tooltip>
                </div>
            </BubbleMenu>

            <div className={classnames(css.content, css.markdown)} style={{minHeight}}>
                <EditorContent editor={editor} />
            </div>
        </div>
    );
};

export default RichTextEditor;
